"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type FormEvent, type ReactNode } from "react";
import { RollWidget, RollWidgetFrame } from "@/components/roll-widgets";
import { useAccountModal } from "@/contexts/account-modal-context";
import { useAuth } from "@/contexts/auth-context";
import { useLiveSession, type LiveStatus } from "@/contexts/live-session-context";
import { useReplay } from "@/contexts/replay-context";
import { ApiError } from "@/lib/api/api-client";
import { isLiveCode, lookupLiveCode } from "@/lib/api/live-api";
import { lookupRecitalCode } from "@/lib/api/recitals-api";
import { recitalPath } from "@/lib/live/recital-routes";
import { InviteIcon, JoinIcon, LoaderIcon, ParticipantsIcon } from "../ui/atoms/icons";
import { CodeInput } from "../ui/molecules/code-input";
import { useQuickRecord } from "./use-quick-record";

/** Même largeur que la console d'un enregistrement, qui prend sa suite. */
const LOBBY_WIDTH_PX = 268;

/**
 * Le widget du coin est-il ouvert : « Seul ou à plusieurs » hors séance, la
 * séance à plusieurs une fois dedans (`JamWidgets`). Un seul état pour les deux,
 * parce qu'un seul picto les ouvre et les ferme, et que l'un prend la place de
 * l'autre quand on invite ou qu'on rejoint.
 *
 * Un magasin de module, comme le premier plan des widgets
 * (`roll-widget-front.ts`) et pour la même raison : le bouton vit dans la
 * barre, les widgets sur le rouleau, et rien ne les surplombe qui porterait un
 * contexte sans traverser tout le studio.
 */
let lobbyOpen = false;
const listeners = new Set<() => void>();

function publish(next: boolean): void {
  if (lobbyOpen === next) {
    return;
  }

  lobbyOpen = next;

  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): boolean {
  return lobbyOpen;
}

function serverSnapshot(): boolean {
  return false;
}

export function useLobbyOpen(): boolean {
  return useSyncExternalStore(subscribe, snapshot, serverSnapshot);
}

export function openLobby(): void {
  publish(true);
}

export function closeLobby(): void {
  publish(false);
}

/** Hors séance : rien d'ouvert, ou une jointure qui a échoué. */
function isIdle(status: LiveStatus): boolean {
  return status === "off" || status === "error";
}

/**
 * Le bouton de la barre qui ouvre « Seul ou à plusieurs ».
 *
 * # Il remplace « Enregistrer »
 *
 * Trois gestes font sortir du studio solo : s'enregistrer, inviter quelqu'un à
 * jouer, rejoindre quelqu'un qui a donné son code. Trois boutons ne tenaient
 * pas dans une barre qui manque déjà de place sur une tablette ; un mot qui les
 * dirait tous les trois n'existe pas — « Live » ne dit pas « enregistrer », et
 * celui qui veut s'enregistrer ne cliquerait pas dessus. Le bouton dit donc les
 * deux choses par leurs signes : la pastille rouge de l'enregistrement, et les
 * silhouettes du jeu à plusieurs. Le clic ouvre le widget, où chaque geste a
 * son nom en toutes lettres.
 *
 * Il a la place de l'ancien, à côté de l'indicateur de live.
 *
 * # Il reste pendant qu'on joue à plusieurs
 *
 * « Inviter » et « Rejoindre » ne ferment rien : le widget de la séance prend
 * la place de celui-ci au même coin, et ce bouton reste pour le refermer et le
 * rouvrir — un widget qu'on ne peut plus ranger finit par cacher le rouleau
 * qu'on est venu jouer. Il se retire dans un récital, qui a sa console à lui,
 * pour un spectateur et devant un replay, où il n'y aurait rien à commencer.
 */
export function LobbyButton() {
  const live = useLiveSession();
  const replay = useReplay();
  const open = useLobbyOpen();

  if (live === null || replay.recitalId !== null || live.isListener) {
    return null;
  }

  const session = live.session;
  const together = session !== null && session.recital === null && live.role === "player";
  // Une invitation en vol : la séance n'a pas encore de billet, et le widget
  // ouvert attend sa réponse. Le bouton reste, sans quoi il clignoterait.
  const inviting = open && session === null;

  if (!isIdle(live.status) && !together && !inviting) {
    return null;
  }

  // En séance, le picto compte qui est là, soi compris — et perd la pastille
  // rouge : rien ne s'enregistre. Compté sur LiveKit et non sur l'audience du
  // service : tout le monde y est visible dans une séance à plusieurs, et il le
  // sait à l'instant, là où l'audience publiée suit à son rythme.
  const present = live.participants.length + 1;
  const label = together
    ? `La séance à plusieurs, ${present} ${present > 1 ? "participants" : "participant"}`
    : "Enregistrer, inviter ou rejoindre";

  return (
    <button
      type="button"
      onClick={() => publish(!open)}
      aria-expanded={open}
      aria-haspopup="dialog"
      aria-label={label}
      title={label}
      /* Même gabarit que l'indicateur de live et le bouton de tempo voisins :
         28 px de haut, cible étendue au doigt par un pseudo-élément. */
      className={`relative inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12px] font-medium tracking-wide transition-colors duration-150 cursor-pointer pointer-coarse:after:absolute pointer-coarse:after:content-[''] pointer-coarse:after:-inset-y-2 pointer-coarse:after:inset-x-0 ${
        open
          ? "text-text-primary bg-white/[0.06]"
          : "text-text-secondary hover:text-text-primary hover:bg-white/[0.04]"
      }`}
    >
      {!together && <span aria-hidden className="size-2 shrink-0 rounded-full bg-red-500" />}
      <ParticipantsIcon size={13} aria-hidden />
      {together && <span className="tabular-nums">{present}</span>}
    </button>
  );
}

/**
 * « Seul ou à plusieurs » : s'enregistrer, inviter, rejoindre — posé sur le
 * rouleau, sous le bouton qui l'ouvre.
 *
 * # On ne quitte pas le studio
 *
 * Inviter ouvre une séance **ici** : le morceau, la vue et le clavier restent,
 * seule l'adresse prend la salle. Rejoindre fait pareil avec la salle qu'un
 * code désigne. Seul l'enregistrement change d'adresse, parce qu'il passe par
 * un récital — et revient aussitôt au studio, dans son sas.
 *
 * # Un seul champ pour tous les codes
 *
 * Celui d'une séance où l'on joue à plusieurs, et celui d'un récital privé :
 * la personne qui a reçu six caractères ne sait pas lesquels, et n'a pas à le
 * savoir. Le code d'une séance fait entrer tout de suite ; celui d'un récital
 * mène à sa salle d'attente, qui est sa seule porte. Les tirages évitent les
 * deux tables côté API, donc un code ne désigne jamais deux choses.
 *
 * # Il cède sa place quand la séance commence
 *
 * La séance à plusieurs a son widget au même coin (`JamWidgets`), ouvert et
 * fermé par le même picto ; un enregistrement a sa console à lui
 * (`recital-widgets.tsx`), et ce widget-ci se ferme. Rien ne le rouvre tout
 * seul en sortant d'une séance.
 */
export function LobbyWidget() {
  const live = useLiveSession();
  const replay = useReplay();
  const open = useLobbyOpen();
  const router = useRouter();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { openAccountModal } = useAccountModal();
  const quick = useQuickRecord();

  const [inviting, setInviting] = useState(false);
  const [inviteFailed, setInviteFailed] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);

  const idle = live === null || isIdle(live.status);

  const invite = useCallback(async () => {
    if (live === null || authLoading) {
      return;
    }

    setInviting(true);
    setInviteFailed(false);

    try {
      // Ouverte, la séance a son widget au même coin : le magasin reste
      // ouvert, et c'est lui qui s'affiche.
      if (!(await live.invite())) {
        // Sans compte, l'écran de compte est déjà là : rien à dire de plus.
        setInviteFailed(isAuthenticated);
      }
    } finally {
      setInviting(false);
    }
  }, [authLoading, isAuthenticated, live]);

  const record = useCallback(async () => {
    if (await quick.record()) {
      closeLobby();
    }
  }, [quick]);

  const toggleCode = useCallback(() => {
    if (authLoading) {
      return;
    }

    // Retrouver une séance par son code exige un compte, comme y entrer : on
    // le demande avant de faire taper six caractères pour rien.
    if (!isAuthenticated) {
      openAccountModal("live_jam");

      return;
    }

    setCodeOpen((current) => !current);
  }, [authLoading, isAuthenticated, openAccountModal]);

  // Comme pour une invitation : le widget de la séance prendra la place.
  const enter = useCallback(
    (room: string) => {
      live?.enter(room);
    },
    [live],
  );

  const visit = useCallback(
    (recitalId: string) => {
      closeLobby();
      router.push(recitalPath(recitalId));
    },
    [router],
  );

  if (!open || live === null || replay.recitalId !== null || live.isListener || (!idle && !inviting)) {
    return null;
  }

  return (
    <RollWidget anchor="top-right">
      <RollWidgetFrame
        kicker="Seul ou à plusieurs"
        width={LOBBY_WIDTH_PX}
        ariaLabel="Enregistrer, inviter ou rejoindre"
        onClose={closeLobby}
      >
        <div className="flex flex-col px-2 py-2">
          <Gesture
            mark={<span className={`size-2 rounded-full bg-red-500 ${quick.busy ? "motion-safe:animate-pulse" : ""}`} />}
            title="Enregistrer"
            hint={quick.busy ? "Préparation…" : (quick.failure ?? "Seul·e, sans public")}
            failed={quick.failure !== null && !quick.busy}
            busy={quick.busy}
            onClick={() => void record()}
          />

          <Gesture
            mark={inviting ? <LoaderIcon size={15} className="animate-spin" /> : <InviteIcon size={15} strokeWidth={1.75} />}
            title="Inviter"
            hint={
              inviting
                ? "Ouverture de la séance…"
                : inviteFailed
                  ? (live.error ?? "La séance n'a pas pu s'ouvrir. Réessaie.")
                  : "Un code à donner"
            }
            failed={inviteFailed && !inviting}
            busy={inviting}
            onClick={() => void invite()}
          />

          <Gesture
            mark={<JoinIcon size={15} strokeWidth={1.75} />}
            title="Rejoindre"
            hint="J'ai un code"
            expanded={codeOpen}
            onClick={toggleCode}
          />

          {codeOpen && <CodeField onSession={enter} onRecital={visit} />}
        </div>
      </RollWidgetFrame>
    </RollWidget>
  );
}

/**
 * Un geste : son signe, son nom, et ce qu'il fait — ou pourquoi il n'a pas pris.
 *
 * # Une ligne, pas un pavé
 *
 * Deux pavés gris, chacun avec sa pastille teintée, se lisaient comme une liste
 * de réglages : trois boîtes dans une boîte, et rien pour dire lequel compte.
 * Ici le texte porte la hiérarchie — le nom en clair, l'indication en retrait —
 * et le fond ne vient qu'au survol, pour dire où tombera le clic. Les signes
 * sont ceux du bouton de la barre, la pastille rouge et les silhouettes : on
 * retrouve dans le widget ce qu'on a cliqué pour l'ouvrir.
 */
function Gesture({
  mark,
  title,
  hint,
  failed = false,
  busy = false,
  expanded,
  onClick,
}: {
  mark: ReactNode;
  title: string;
  hint: string;
  failed?: boolean;
  busy?: boolean;
  /** Pour le geste qui déplie un champ : son état, lu par les lecteurs d'écran. */
  expanded?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-expanded={expanded}
      className="group flex w-full cursor-pointer items-start gap-3 rounded-lg px-2 py-2.5 text-left transition-colors duration-150 hover:bg-white/[0.05] disabled:cursor-wait"
    >
      {/* Sur la hauteur du nom : le signe se lit avec lui, pas avec l'indication. */}
      <span
        aria-hidden
        className="grid h-5 w-4 shrink-0 place-items-center text-text-secondary transition-colors duration-150 group-hover:text-text-primary"
      >
        {mark}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[14px] font-medium leading-5 text-text-primary">{title}</span>
        <span className={`text-[12px] leading-4 ${failed ? "text-red-300" : "text-text-muted"}`}>{hint}</span>
      </span>
    </button>
  );
}

/**
 * Le code, déplié sous « Rejoindre » : six caractères, et l'endroit où ils
 * mènent.
 *
 * # Six cases
 *
 * Un code se recopie caractère par caractère : une case pour chacun
 * ({@link CodeInput}), sur un trait comme les autres champs du produit, et
 * alignées sur les noms des gestes. Pas de bouton pour envoyer : le code part
 * de lui-même à son sixième caractère, et Entrée le renvoie.
 *
 * # Où il mène
 *
 * La séance d'abord : c'est le cas de ce widget, et le seul où l'on entre sans
 * quitter le studio. Puis le récital, dont la salle d'attente est la seule
 * porte. La casse compte — l'alphabet des codes en fait des lettres
 * différentes —, donc rien ici ne la change : ni majuscules forcées, ni
 * correction du clavier.
 */
function CodeField({
  onSession,
  onRecital,
}: {
  onSession: (room: string) => void;
  onRecital: (recitalId: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Le code en paramètre et non lu dans l'état : au dernier caractère, la
  // recherche part dans le même geste que la frappe, avant que l'état ne suive.
  const lookUp = useCallback(
    async (typed: string) => {
      if (!isLiveCode(typed) || busy) {
        return;
      }

      setBusy(true);
      setError(null);

      try {
        const session = await lookupLiveCode(typed);

        onSession(session.room);

        return;
      } catch (cause) {
        // Autre chose que « aucune séance » : une panne, un plafond. La dire
        // telle quelle plutôt que d'aller chercher un récital par-dessus.
        if (!(cause instanceof ApiError) || cause.status !== 404) {
          setError(cause instanceof Error && cause.message !== "" ? cause.message : "Le code n'a pas pu être vérifié.");
          setBusy(false);

          return;
        }
      }

      try {
        const recital = await lookupRecitalCode(typed);

        onRecital(recital.id);
      } catch {
        setError("Ce code ne mène nulle part. Les majuscules comptent.");
        setBusy(false);
      }
    },
    [busy, onRecital, onSession],
  );

  const submit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      void lookUp(code);
    },
    [code, lookUp],
  );

  return (
    // Aligné sur les noms des gestes : le décalage de la ligne, plus le signe et
    // son écart (`px-2` + `w-4` + `gap-3`). Pas de bouton : le code part tout
    // seul à son dernier caractère, et Entrée le renvoie.
    <form onSubmit={submit} className="flex flex-col gap-2 pb-2.5 pl-9 pr-2">
      <CodeInput
        inputRef={inputRef}
        value={code}
        onChange={(next) => {
          setCode(next);
          setError(null);
        }}
        onComplete={(complete) => void lookUp(complete)}
        invalid={error !== null}
        busy={busy}
        ariaLabel="Code à six caractères"
      />

      {error !== null && (
        <p role="alert" className="m-0 text-[11px] leading-snug text-red-300">
          {error}
        </p>
      )}
    </form>
  );
}
